/**
 * SecretsRouter — audit chokepoint for all secret operations.
 *
 * Registers {@link ISecretsProvider} implementations by id, routes credential refs by
 * prefix or via routing config, and emits {@link CredentialAccessed} events for every
 * get, set, delete, list, and metadata operation. Routing rules are evaluated top-to-bottom;
 * the first matching prefix wins and falls back to the configured default provider.
 *
 * @docLink packages/secrets/concepts#secrets-router
 */

import type {
  ISecretsProvider,
  ListFilter,
  ProviderCapabilities,
  SecretMetadata,
  SetSecretOptions,
} from "@skaile/workspaces/plugins";
import { ProviderUnavailableError } from "@skaile/workspaces/plugins";

// ---------------------------------------------------------------------------
// Audit event
// ---------------------------------------------------------------------------

/**
 * Audit event emitted by {@link SecretsRouter} after every secret operation.
 *
 * Delivered synchronously to all registered {@link AuditSink} functions. Errors thrown
 * by sinks are swallowed to ensure they never break the underlying secret operation.
 *
 * @docLink packages/secrets/concepts#secrets-router
 */
export interface CredentialAccessed {
  credential_ref: string;
  operation: "get" | "set" | "delete" | "list" | "metadata";
  provider_id: string;
  caller_session_id?: string;
  caller_tool_call?: string;
  timestamp: Date;
}

/**
 * Callback function that receives {@link CredentialAccessed} audit events from {@link SecretsRouter}.
 *
 * Register sinks via `SecretsRouter.addSink()`. Sinks run synchronously after each operation;
 * exceptions are caught and suppressed so they cannot interrupt credential resolution.
 *
 * @docLink packages/secrets/concepts#secrets-router
 */
export type AuditSink = (event: CredentialAccessed) => void;

// ---------------------------------------------------------------------------
// Routing config
// ---------------------------------------------------------------------------

/**
 * A single routing rule that maps a credential ref prefix to a provider.
 *
 * Rules in {@link SecretsRouterConfig.rules} are evaluated top-to-bottom;
 * the first rule whose `prefix` matches the start of the credential ref wins.
 *
 * @docLink packages/secrets/concepts#secrets-router
 */
export interface RoutingRule {
  /** Prefix match on credential_ref. First match wins. */
  prefix: string;
  /** Provider id to route to. */
  provider_id: string;
}

/**
 * Configuration object for {@link SecretsRouter}.
 *
 * Specifies the default provider id used when no routing rule matches, and an optional
 * ordered list of {@link RoutingRule} entries for prefix-based routing.
 *
 * @docLink packages/secrets/concepts#secrets-router
 */
export interface SecretsRouterConfig {
  /** Default provider id when no routing rule matches. */
  default_provider: string;
  /** Ordered routing rules. Evaluated top-to-bottom; first prefix match wins. */
  rules?: RoutingRule[];
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export class SecretsRouter {
  private providers = new Map<string, ISecretsProvider>();
  private config: SecretsRouterConfig;
  private sinks: AuditSink[] = [];

  /** Caller context for audit events (set per-request by the runner). */
  callerSessionId?: string;
  callerToolCall?: string;

  constructor(config: SecretsRouterConfig) {
    this.config = config;
  }

  // -- Provider registration ------------------------------------------------

  register(provider: ISecretsProvider): void {
    this.providers.set(provider.id, provider);
  }

  unregister(id: string): void {
    this.providers.delete(id);
  }

  listProviders(): string[] {
    return [...this.providers.keys()];
  }

  // -- Audit sinks -----------------------------------------------------------

  addSink(sink: AuditSink): void {
    this.sinks.push(sink);
  }

  private emit(ref: string, operation: CredentialAccessed["operation"], providerId: string): void {
    const event: CredentialAccessed = {
      credential_ref: ref,
      operation,
      provider_id: providerId,
      caller_session_id: this.callerSessionId,
      caller_tool_call: this.callerToolCall,
      timestamp: new Date(),
    };
    for (const sink of this.sinks) {
      try {
        sink(event);
      } catch {
        // Audit sink errors must not break the secret operation.
      }
    }
  }

  // -- Routing ---------------------------------------------------------------

  private resolve(ref: string): ISecretsProvider {
    // Check routing rules first
    if (this.config.rules) {
      for (const rule of this.config.rules) {
        if (ref.startsWith(rule.prefix)) {
          const provider = this.providers.get(rule.provider_id);
          if (provider) return provider;
        }
      }
    }

    // Fall back to default
    const provider = this.providers.get(this.config.default_provider);
    if (!provider) {
      throw new ProviderUnavailableError(
        this.config.default_provider,
        `No provider registered for default id "${this.config.default_provider}"`,
      );
    }
    return provider;
  }

  // -- Delegated operations --------------------------------------------------

  async getSecret(ref: string): Promise<Uint8Array> {
    const provider = this.resolve(ref);
    this.emit(ref, "get", provider.id);
    return provider.getSecret(ref);
  }

  async setSecret(ref: string, value: Uint8Array, opts?: SetSecretOptions): Promise<void> {
    const provider = this.resolve(ref);
    this.emit(ref, "set", provider.id);
    return provider.setSecret(ref, value, opts);
  }

  async deleteSecret(ref: string): Promise<void> {
    const provider = this.resolve(ref);
    this.emit(ref, "delete", provider.id);
    return provider.deleteSecret(ref);
  }

  async listSecrets(filter?: ListFilter): Promise<string[]> {
    // List aggregates from the default provider only (per-provider listing
    // would require iterating all providers; deferred to Phase 3 with
    // federated catalogs).
    const provider = this.providers.get(this.config.default_provider);
    if (!provider) return [];
    this.emit("*", "list", provider.id);
    return provider.listSecrets(filter);
  }

  async getMetadata(ref: string): Promise<SecretMetadata> {
    const provider = this.resolve(ref);
    this.emit(ref, "metadata", provider.id);
    return provider.getMetadata(ref);
  }

  getCapabilities(providerId?: string): ProviderCapabilities | null {
    const id = providerId ?? this.config.default_provider;
    const provider = this.providers.get(id);
    return provider?.getCapabilities() ?? null;
  }

  async testConnection(
    providerId?: string,
  ): Promise<{ ok: boolean; latencyMs?: number; error?: string }> {
    const id = providerId ?? this.config.default_provider;
    const provider = this.providers.get(id);
    if (!provider) {
      return { ok: false, error: `Provider "${id}" not registered` };
    }
    return provider.testConnection();
  }
}
